import { Icon } from "../icons/Icon";
import type { NavPage } from "../../config/roleMenus";

/**
 * 移动端底部导航（≤768px）。
 *
 * 为什么需要它：窄屏下顶栏要同时容纳「菜单按钮 + 业务域页签 + 搜索 + 通知 + 头像」，
 * 实测 390px 时页签区只剩 **139px** 而内容宽 **425px**（6 个域必须横滑才能看全）。
 * 底部栏把 4 个高频页面固定在拇指可达区，其余页面走「更多」抽屉（域分组）。
 *
 * 选页策略（不是简单取前 4 个）：按**现场高频**优先级从「当前角色可见页面」里挑，
 * 保证操作员/生产主管拿到的是 驾驶舱 / 车间执行 / 安灯 / 审批，而不是菜单文件里的先后顺序。
 * 可见页面不足 4 个时不渲染「更多」（没有意义的多一层）。
 */
const PRIORITY = ["/", "/workshop", "/andon", "/approval", "/inventory", "/production", "/quality"];

export interface MobileTabBarProps {
  pages: NavPage[];
  activeKey: string;
  onNavigate: (key: string) => void;
  onOpenMore: () => void;
}

/** 从可见页面里按优先级挑出底栏展示的页面（导出供单测） */
export function pickTabBarPages(pages: NavPage[], max = 4): NavPage[] {
  if (pages.length <= max) return pages;
  const picked: NavPage[] = [];
  PRIORITY.forEach((key) => {
    if (picked.length >= max) return;
    const page = pages.find((item) => item.key === key);
    if (page) picked.push(page);
  });
  // 优先级页不够（例如质检员只看得到驾驶舱/库存/质量）：按菜单顺序补齐
  for (const page of pages) {
    if (picked.length >= max) break;
    if (!picked.some((item) => item.key === page.key)) picked.push(page);
  }
  return picked;
}

export default function MobileTabBar({ pages, activeKey, onNavigate, onOpenMore }: MobileTabBarProps) {
  const items = pickTabBarPages(pages);
  const hasMore = pages.length > items.length;
  const moreActive = hasMore && !items.some((page) => page.key === activeKey);

  return (
    <nav className="mobile-tabbar" aria-label="底部导航">
      {items.map((page) => {
        const active = page.key === activeKey;
        return (
          <button
            key={page.key}
            type="button"
            className={`mobile-tabbar-item${active ? " is-active" : ""}`}
            aria-current={active ? "page" : undefined}
            onClick={() => onNavigate(page.key)}
          >
            {page.icon}
            <span className="mobile-tabbar-label">{page.label}</span>
          </button>
        );
      })}
      {hasMore ? (
        <button
          type="button"
          className={`mobile-tabbar-item${moreActive ? " is-active" : ""}`}
          aria-label="更多页面"
          onClick={onOpenMore}
        >
          <Icon name="appstore" size={18} />
          <span className="mobile-tabbar-label">更多</span>
        </button>
      ) : null}
    </nav>
  );
}
